import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './JobLogsDetail.module.css';
import { jobApi, JobLogDetail } from '../../services/jobApi';

const JobLogsDetail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const runId = searchParams.get('run_id');
  const [logDetail, setLogDetail] = useState<JobLogDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    if (runId) {
      loadLogDetail();
    } else {
      toast.error('No run ID provided', {
        position: "top-right",
        autoClose: 5000
      });
      setLoading(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [runId]);

  const loadLogDetail = async () => {
    if (!runId) return;

    try {
      setLoading(true);
  const data = await jobApi.getJobRunDetail(runId);
      if (isMountedRef.current) {
        setLogDetail(data);
      }
    } catch (error) {
      console.error('Error loading log details:', error);
      toast.error('Failed to load log details. Please try again later.', {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString()
      .padStart(2, '0')}`;
  };

  const getLogLevelBadgeClass = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return 'bg-danger';
      case 'WARNING':
      case 'WARN':
        return 'bg-warning';
      case 'INFO':
        return 'bg-info';
      case 'DEBUG':
        return 'bg-secondary';
      default:
        return 'bg-primary';
    }
  };

  if (loading) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading log details...</div>
      </div>
    );
  }

  if (!logDetail) {
    return (
      <div className={`container mt-5 ${styles.errorContainer}`}>
        <div className="alert alert-danger">
          <h4>Log Details Not Found</h4>
          <p>The requested log details could not be loaded.</p>
          <Link to="/admin/job-logs" className="btn btn-primary">
            Back to Job Logs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/admin/job-logs">Job Log Management</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">Detail View</li>
          </ol>
        </nav>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Log Detail View</h2>
            <p className="text-muted mb-0">
              <i className="bi bi-file-text me-2"></i>
              Job Run ID: <span className="fw-bold">{logDetail.job_run_id}</span>
            </p>
          </div>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-outline-primary btn-sm"
              onClick={loadLogDetail}
              title="Refresh logs"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
            <div className="d-flex align-items-center">
              <i className="bi bi-list-ul me-2 text-primary"></i>
              <h5 className="mb-0">Log Entries</h5>
            </div>
            <span className="badge bg-primary rounded-pill">
              {logDetail.logs.length} {logDetail.logs.length === 1 ? 'Entry' : 'Entries'}
            </span>
          </div>
          
          <table className={`table ${styles.logDetailTable}`}>
            <thead>
              <tr>
                <th>
                  <i className="bi bi-clock me-1"></i>
                  Timestamp
                </th>
                <th>
                  <i className="bi bi-flag me-1"></i>
                  Level
                </th>
                <th>
                  <i className="bi bi-chat-quote me-1"></i>
                  Message
                </th>
              </tr>
            </thead>
            <tbody>
              {logDetail.logs.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center p-5">
                    <div className={styles.emptyState}>
                      <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                      <h5 className="text-muted">No log entries found</h5>
                      <p className="text-muted mb-0">
                        This job run doesn't have any log entries yet.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                logDetail.logs.map((log, index) => (
                  <tr key={log.id} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                    <td className={styles.logDetailTableCell}>
                      <div className={styles.timestampContainer}>
                        <span className={styles.timestamp}>
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                    </td>
                    <td className={styles.logDetailTableCell}>
                      <span className={`badge ${getLogLevelBadgeClass(log.log_level)} ${styles.levelBadge}`}>
                        {log.log_level}
                      </span>
                    </td>
                    <td className={styles.logDetailTableCell}>
                      <div className={styles.messageContainer}>
                        <pre className={styles.logMessage}>{log.message}</pre>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center">
            <Link to="/admin/job-logs" className="btn btn-secondary">
              <i className="bi bi-arrow-left me-2"></i>
              Back to Job Logs
            </Link>
            
            <div className="text-muted small">
              <i className="bi bi-info-circle me-1"></i>
              Showing {logDetail.logs.length} log {logDetail.logs.length === 1 ? 'entry' : 'entries'}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999 }}
        limit={3}
      />
    </div>
  );
};

export default JobLogsDetail;
